import { useState, useEffect, useRef, type FormEvent, type ChangeEvent } from 'react';
import AccountLayout from '@/components/feature/AccountLayout';
import WithdrawPasswordCard from '@/pages/kyc/components/WithdrawPasswordCard';
import { supabase } from '@/lib/supabase';

interface KycData {
  id: string;
  id_type: string;
  id_number: string | null;
  full_name_on_id: string | null;
  date_of_birth: string | null;
  id_front_image: string | null;
  id_back_image: string | null;
  selfie_image: string | null;
  status: string;
  rejection_reason: string | null;
  submitted_at: string;
}

const statusMeta: Record<string, { label: string; cls: string }> = {
  pending: { label: 'Đang chờ xét duyệt', cls: 'bg-accent-500/15 text-accent-400' },
  approved: { label: 'Đã xác minh', cls: 'bg-emerald-500/15 text-emerald-400' },
  rejected: { label: 'Bị từ chối', cls: 'bg-rose-500/15 text-rose-400' },
};

export default function Kyc() {
  const [kyc, setKyc] = useState<KycData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [idType, setIdType] = useState('CCCD');
  const [idNumber, setIdNumber] = useState('');
  const [fullName, setFullName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');

  const [frontPreview, setFrontPreview] = useState<string | null>(null);
  const [backPreview, setBackPreview] = useState<string | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null);
  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [backFile, setBackFile] = useState<File | null>(null);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);

  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);
  const selfieInputRef = useRef<HTMLInputElement>(null);

  const loadKyc = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user.id;
      if (!userId) return;

      const { data: kycRaw, error: kycError } = await supabase.rpc('get_user_kyc_json', { p_user_id: userId });
      if (kycError) throw kycError;
      const kycArr = Array.isArray(kycRaw) ? kycRaw : JSON.parse((kycRaw as string) || '[]');
      setKyc(kycArr[0] || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải dữ liệu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKyc();
  }, []);

  const handleFile = (
    e: ChangeEvent<HTMLInputElement>,
    setPreview: (v: string | null) => void,
    setFile: (f: File | null) => void
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Ảnh vượt quá 5MB. Vui lòng chọn ảnh nhỏ hơn.');
      return;
    }
    setFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const uploadImage = async (file: File, userId: string, folder: string) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const safeExt = ['jpg', 'jpeg', 'png', 'webp'].includes(ext) ? ext : 'jpg';
    const path = `${folder}/${userId}/${Date.now()}.${safeExt}`;
    const { error: uploadError } = await supabase.storage.from('private').upload(path, file, {
      cacheControl: '3600',
      upsert: false,
    });
    if (uploadError) throw uploadError;
    return path;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!idNumber.trim()) {
      setError('Vui lòng nhập số CMND/CCCD.');
      return;
    }
    if (!fullName.trim()) {
      setError('Vui lòng nhập họ tên trên giấy tờ.');
      return;
    }
    if (!dateOfBirth) {
      setError('Vui lòng chọn ngày sinh.');
      return;
    }
    if (!frontFile || !backFile || !selfieFile) {
      setError('Vui lòng tải đủ 3 ảnh: mặt trước, mặt sau và ảnh chân dung.');
      return;
    }

    setSubmitting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user.id;
      if (!userId) throw new Error('Bạn chưa đăng nhập.');

      const frontPath = await uploadImage(frontFile, userId, 'kyc');
      const backPath = await uploadImage(backFile, userId, 'kyc');
      const selfiePath = await uploadImage(selfieFile, userId, 'kyc');

      const { error: insertError } = await supabase.from('kyc_verifications').insert({
        user_id: userId,
        id_type: idType,
        id_number: idNumber.trim(),
        full_name_on_id: fullName.trim(),
        date_of_birth: dateOfBirth,
        id_front_image: frontPath,
        id_back_image: backPath,
        selfie_image: selfiePath,
        status: 'pending',
      });

      if (insertError) throw insertError;

      setSuccess('Đã gửi hồ sơ xác minh thành công. Hệ thống sẽ xét duyệt trong vòng 24 giờ.');
      setFrontFile(null);
      setBackFile(null);
      setSelfieFile(null);
      setFrontPreview(null);
      setBackPreview(null);
      setSelfiePreview(null);
      setIdNumber('');
      setFullName('');
      setDateOfBirth('');
      await loadKyc();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gửi hồ sơ thất bại.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500';

  const labelCls = 'block text-xs font-semibold text-foreground-600 mb-1.5';

  const renderUploadBox = (
    label: string,
    hint: string,
    preview: string | null,
    inputRef: React.RefObject<HTMLInputElement | null>,
    onFile: (e: ChangeEvent<HTMLInputElement>) => void
  ) => (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="w-full rounded-md border-2 border-dashed border-background-300/60 bg-background-50 p-4 flex flex-col items-center justify-center cursor-pointer hover:border-primary-400 transition-colors"
      >
        {preview ? (
          <img src={preview} alt={label} className="w-full max-h-40 object-contain rounded-md" />
        ) : (
          <>
            <i className="ri-camera-line text-2xl text-foreground-500"></i>
            <span className="mt-2 text-sm font-semibold text-foreground-700">Tải ảnh lên</span>
            <span className="text-xs text-foreground-500">{hint}</span>
          </>
        )}
      </button>
    </div>
  );

  return (
    <AccountLayout>
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-foreground-950 mb-2">Xác minh danh tính</h1>
        <p className="text-foreground-600 mb-6">Hoàn tất xác minh để nạp/rút tiền và giao dịch không giới hạn.</p>

        <WithdrawPasswordCard />

        {error && (
          <div className="mb-4 rounded-lg bg-rose-500/15 border border-rose-500/30 p-3 flex items-center gap-2">
            <i className="ri-error-warning-line text-rose-400 text-base"></i>
            <p className="text-sm text-rose-400">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 rounded-lg bg-emerald-500/15 border border-emerald-500/30 p-3 flex items-center gap-2">
            <i className="ri-check-line text-emerald-400 text-base"></i>
            <p className="text-sm text-emerald-400">{success}</p>
          </div>
        )}

        {loading ? (
          <div className="py-10 text-center text-sm text-foreground-600">Đang tải...</div>
        ) : kyc && kyc.status !== 'rejected' ? (
          <div className="rounded-xl border border-background-200/60 bg-background-100 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-lg bg-accent-500/15 flex items-center justify-center">
                <i className="ri-shield-check-line text-accent-400 text-2xl"></i>
              </div>
              <div>
                <p className="text-base font-semibold text-foreground-950">Hồ sơ xác minh của bạn</p>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${statusMeta[kyc.status]?.cls || 'bg-foreground-200/40 text-foreground-700'}`}>
                  {statusMeta[kyc.status]?.label || kyc.status}
                </span>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-foreground-600">Loại giấy tờ</span>
                <span className="font-semibold text-foreground-950">{kyc.id_type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-600">Số giấy tờ</span>
                <span className="font-semibold text-foreground-950">
                  {kyc.id_number ? `${kyc.id_number.slice(0, 4)}******` : '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-600">Họ tên</span>
                <span className="font-semibold text-foreground-950">{kyc.full_name_on_id || '-'}</span>
              </div>
            </div>
            {kyc.status === 'pending' && (
              <p className="mt-4 text-sm text-foreground-600">
                <i className="ri-time-line mr-1"></i> Hồ sơ đang được xét duyệt. Vui lòng chờ trong 24 giờ.
              </p>
            )}
          </div>
        ) : (
          <>
            {kyc?.status === 'rejected' && (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 mb-6">
                <p className="text-sm font-semibold text-rose-400">Hồ sơ trước đó bị từ chối.</p>
                {kyc.rejection_reason && (
                  <p className="text-sm text-foreground-700 mt-1">Lý do: {kyc.rejection_reason}</p>
                )}
                <p className="text-xs text-foreground-600 mt-2">Vui lòng gửi lại hồ sơ với thông tin chính xác.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="rounded-xl border border-background-200/60 bg-background-100 p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Loại giấy tờ</label>
                  <select value={idType} onChange={(e) => setIdType(e.target.value)} className={inputCls}>
                    <option value="CCCD">Căn cước công dân (CCCD)</option>
                    <option value="CMND">Chứng minh nhân dân (CMND)</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Số CMND/CCCD</label>
                  <input
                    value={idNumber}
                    onChange={(e) => setIdNumber(e.target.value)}
                    placeholder="VD: 001234567890"
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Họ tên trên giấy tờ</label>
                  <input
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="VD: NGUYEN VAN A"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Ngày sinh</label>
                  <input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {renderUploadBox(
                  'Mặt trước',
                  'Ảnh mặt trước CMND/CCCD',
                  frontPreview,
                  frontInputRef,
                  (e) => handleFile(e, setFrontPreview, setFrontFile)
                )}
                {renderUploadBox(
                  'Mặt sau',
                  'Ảnh mặt sau CMND/CCCD',
                  backPreview,
                  backInputRef,
                  (e) => handleFile(e, setBackPreview, setBackFile)
                )}
                {renderUploadBox(
                  'Ảnh chân dung',
                  'Ảnh selfie rõ mặt',
                  selfiePreview,
                  selfieInputRef,
                  (e) => handleFile(e, setSelfiePreview, setSelfieFile)
                )}
              </div>

              <p className="text-xs text-foreground-500 flex items-center gap-1">
                <i className="ri-shield-keyhole-line"></i> Thông tin và hình ảnh của bạn được bảo mật tuyệt đối, chỉ dùng cho mục đích xác minh danh tính.
              </p>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors"
              >
                {submitting ? 'Đang gửi hồ sơ...' : 'Gửi hồ sơ xác minh'}
              </button>
            </form>
          </>
        )}
      </div>
    </AccountLayout>
  );
}